import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export function AuditRecordFilter() {
  const nav = useNavigate({ from: "/app/internal-audit/records" });
  const search = useSearch({ from: "/app/internal-audit/records" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "auditScope");
  const overallResultFilter = findCondition(filters, "overallResult");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((searchFilter?.value as string) || ""),
    [searchFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "auditScope",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "auditScope");
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const overallResultValue = (overallResultFilter?.value as string) || "";
  const statusValue = (statusFilter?.value as string) || "";

  const hasFilter =
    !!searchFilter?.value || !!overallResultValue || !!statusValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="record-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ (ສິ່ງທີ່ກວດສອບ)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={overallResultValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "overallResult");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "overallResult",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຜົນການກວດສອບ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "pass", label: "ຜ່ານ" },
              { value: "fail", label: "ບໍ່ຜ່ານ" },
              { value: "needs_improvement", label: "ຕ້ອງປັບປຸງ" },
            ]}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "draft", label: "ຮ່າງ" },
              { value: "completed", label: "ແລ້ວ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}

